
import { computed, defineComponent, onBeforeMount, reactive, watch } from 'vue'
import ymaps from 'yandex-maps'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// @new-ui
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'

import NewMap from '@/common/new-components/NewMap/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'

// @modules/components
import MonitoringStats from '@/modules/NewDispatcher/components/MonitoringStats/index.vue'
import TypeSwitcher from '@/modules/NewDispatcher/components/TypeSwitcher/index.vue'

// @types
import { DispatcherSector } from '@/new-services/types/dispatcher-sectors'
import {
    MonitoringStatistics,
    MonitoringFilteredStatistics,
    MonitoringFilteredStatisticsRecord,
    MonitoringFilteredCourierStatistics,
} from '@/new-services/types/monitoring'
import { Sector } from '@/new-services/types/sectors'
import { DeliveryOrder } from '@/new-services/types/delivery'
import { TakeOrder } from '@/new-services/types/takes'

type GetPlacemarkOptions = {
    order: DeliveryOrder | TakeOrder
    color: string
}

// @services
import monitoringAPI from '@/new-services/monitoring.service'
import sectorsAPI from '@/new-services/sectors.service'
import takeAPI from '@/new-services/takes.service'
import deliveryAPI from '@/new-services/delivery.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import useMap from '@/common/new-composable/useMap'
import useMapGeoObjects from '@/common/new-composable/useMapGeoObjects'

// @store
import { useNewDispatcherStore } from '@/modules/NewDispatcher/stores/useNewDispatcherStore'

// @constants
const MAP_ORDERS_TYPES = [
    { id: 1, name: 'Доставки' },
    { id: 2, name: 'Заборы' },
]

const PLACEMARK_COLORS = {
    GREEN: '#44F805',
    YELLOW: '#FDCF00',
    RED: '#EA1313',
}

export default defineComponent({
    components: {
        PageTitle,
        NewInput,
        NewButton,
        Loader,
        NewMap,
        NewCheckbox,
        MonitoringStats,
        TypeSwitcher,
    },
    setup() {
        const new_dispatcher_store = useNewDispatcherStore()

        const notifications = useNotifications()
        const { clearMap, addGeoObject, setMapCenter } = useMap()
        const map_graphics = useMapGeoObjects()

        let map_instance = null as ymaps.Map | null

        const initialMapOrdersTypes = MAP_ORDERS_TYPES.map((type) => type.id)

        const state = reactive({
            is_map_ready: false,
            map_orders_types: [...initialMapOrdersTypes] as number[],
        })

        const filters = reactive({
            createdAtFrom: null as string | null,
            createdAtTo: null as string | null,
        })

        const deliveries_state = reactive({
            is_loading: false,
            is_failed: false,
            stats: [] as MonitoringFilteredStatistics,
        })

        const takes_state = reactive({
            is_loading: false,
            is_failed: false,
            stats: [] as MonitoringFilteredStatistics,
        })

        const couriers_state = reactive({
            is_loading: false,
            is_failed: false,
            stats: [] as MonitoringFilteredCourierStatistics,
        })

        const sectors_list_state = reactive({
            is_loading: false,
            is_failed: false,
            options: [] as Sector[],
        })

        const delivery_completed_orders_state = reactive({
            is_showed: true,
            is_loading: false,
            orders: [] as DeliveryOrder[],
        })

        const delivery_cancelled_orders_state = reactive({
            is_showed: true,
            is_loading: false,
            orders: [] as DeliveryOrder[],
        })

        const delivery_remained_orders_state = reactive({
            is_showed: true,
            is_loading: false,
            orders: [] as DeliveryOrder[],
        })

        const takes_completed_orders_state = reactive({
            is_showed: true,
            is_loading: false,
            orders: [] as TakeOrder[],
        })

        const takes_cancelled_orders_state = reactive({
            is_showed: true,
            is_loading: false,
            orders: [] as TakeOrder[],
        })

        const takes_remained_orders_state = reactive({
            is_showed: true,
            is_loading: false,
            orders: [] as TakeOrder[],
        })

        const filterStatistics = ({
            statistics,
            filterBy,
        }: {
            statistics: MonitoringStatistics
            filterBy: 'name' | 'sectorId'
        }): MonitoringFilteredStatistics => {
            const temp_array = [] as MonitoringFilteredStatisticsRecord[]

            statistics.total.forEach((statistics) => {
                const index = temp_array.findIndex(
                    (temp_item) => temp_item[filterBy] === statistics[filterBy]
                )

                if (index < 0) {
                    temp_array.push({
                        name: statistics.name,
                        sectorId: statistics.sectorId,
                        cancelled: 0,
                        completed: 0,
                        remained: 0,
                        total: 0,
                    })
                }
            })

            statistics.cancelled.forEach((row) => {
                const index = temp_array.findIndex(
                    (temp_item) => temp_item[filterBy] === row[filterBy]
                )
                if (index >= 0) {
                    temp_array[index].cancelled = temp_array[index].cancelled + row.count
                }
            })

            statistics.completed.forEach((row) => {
                const index = temp_array.findIndex(
                    (temp_item) => temp_item[filterBy] === row[filterBy]
                )
                if (index >= 0) {
                    temp_array[index].completed = temp_array[index].completed + row.count
                }
            })

            statistics.remained.forEach((row) => {
                const index = temp_array.findIndex(
                    (temp_item) => temp_item[filterBy] === row[filterBy]
                )
                if (index >= 0) {
                    temp_array[index].remained = temp_array[index].remained + row.count
                }
            })

            statistics.total.forEach((row) => {
                const index = temp_array.findIndex(
                    (temp_item) => temp_item[filterBy] === row[filterBy]
                )
                if (index >= 0) {
                    temp_array[index].total = temp_array[index].total + row.count
                }
            })

            return temp_array
        }

        const loadDeliveries = async () => {
            try {
                deliveries_state.is_loading = true

                let dispatcherSectorIds: number[] = []

                if (new_dispatcher_store.dispatcherSectorIds.length) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const params = {
                    dispatcherSectorIds,
                    createdAtFrom: filters.createdAtFrom,
                    createdAtTo: filters.createdAtTo,
                }

                const response = await monitoringAPI.getDeliveries(params)
                deliveries_state.stats = filterStatistics({
                    statistics: response.data.data,
                    filterBy: 'sectorId',
                })

                deliveries_state.is_failed = false
            } catch (error) {
                console.error(error)
                deliveries_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении статистики доставок произошла ошибка',
                })
            } finally {
                deliveries_state.is_loading = false
            }
        }

        const loadTakes = async () => {
            try {
                takes_state.is_loading = true

                let dispatcherSectorIds: number[] = []

                if (new_dispatcher_store.dispatcherSectorIds.length) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const params = {
                    dispatcherSectorIds,
                    takeDateFrom: filters.createdAtFrom,
                    takeDateTo: filters.createdAtTo,
                }

                const response = await monitoringAPI.getTakes(params)
                takes_state.stats = filterStatistics({
                    statistics: response.data.data,
                    filterBy: 'sectorId',
                })

                takes_state.is_failed = false
            } catch (error) {
                console.error(error)
                takes_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении статистики заборов произошла ошибка',
                })
            } finally {
                takes_state.is_loading = false
            }
        }

        const loadCouriers = async () => {
            try {
                couriers_state.is_loading = true

                let dispatcherSectorIds: number[] = []

                if (new_dispatcher_store.dispatcherSectorIds.length) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const params = {
                    dispatcherSectorIds,
                    createdAtFrom: filters.createdAtFrom,
                    createdAtTo: filters.createdAtTo,
                }

                const response = await monitoringAPI.getCouriers(params)
                couriers_state.stats = response.data.data

                couriers_state.is_failed = false
            } catch (error) {
                console.error(error)
                couriers_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении статистики курьеров произошла ошибка',
                })
            } finally {
                couriers_state.is_loading = false
            }
        }

        const drawSector = (sector: Sector) => {
            const polygon = map_graphics.createPolygon({
                coordinates: JSON.parse(sector.coordinates),
                color: sector.color,
                hintContent: `Сектор ${sector.name}`,
            })

            addGeoObject(map_instance as ymaps.Map, polygon)
        }

        const drawSectors = (sectors: Sector[]) => {
            if (!map_instance) return
            sectors.forEach((sector) => {
                if (sector.coordinates !== 'null') drawSector(sector)
            })
        }

        const loadDeliverySectors = async () => {
            try {
                sectors_list_state.is_loading = true

                let dispatcherSectorIds: number[] | undefined = undefined

                if (new_dispatcher_store.dispatcherSectorIds.length) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const response = await sectorsAPI.getSectors({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds,
                })

                sectors_list_state.options = response.data.data

                drawSectors(sectors_list_state.options)

                sectors_list_state.is_failed = false
            } catch (error) {
                sectors_list_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке секторов доставки произошла ошибка',
                })
            } finally {
                sectors_list_state.is_loading = false
            }
        }

        const setMapCenterOnSector = (sector: Sector) => {
            const centroid = map_graphics.getPolygonCentroid(JSON.parse(sector.coordinates))
            setMapCenter(map_instance as ymaps.Map, centroid)
        }

        const setMapCenterBySelectedDispatcherSector = (dispatcher_sector: DispatcherSector) => {
            if (dispatcher_sector.city) {
                if (dispatcher_sector.city.latitude && dispatcher_sector.city.longitude) {
                    setMapCenter(map_instance as ymaps.Map, [
                        dispatcher_sector.city.latitude,
                        dispatcher_sector.city.longitude,
                    ])
                    return
                }
            }

            const centroid = map_graphics.getPolygonCentroid(
                JSON.parse(dispatcher_sector.coordinates)
            )
            setMapCenter(map_instance as ymaps.Map, centroid)
        }

        const getTakePlacemark = (
            options: GetPlacemarkOptions & { order: TakeOrder }
        ): ymaps.Placemark => {
            const coordinates = [Number(options.order.latitude), Number(options.order.longitude)]

            const address = options.order.customer ? options.order.customer.address : 'Не указан'
            const courier = options.order.courier ? options.order.courier.fullName : 'Не указан'
            const status = options.order.status ? options.order.status.title : 'Не указан'

            const props = {
                balloonContentHeader: `<div class="newballoon__header">${options.order.orderNumber}</div>`,
                balloonContentBody: `<div class="newballoon__body">
                    <div class="grid grid-cols-2 gap-1 items-center">
                        <div class="body__label">Клиент: </div>
                        <div class="body__value">${options.order.companyName}</div>

                        <div class="body__label">Адрес: </div>
                        <div class="body__value">${address}</div>

                        <div class="body__label">Количество накладных: </div>
                        <div class="body__value">${options.order.invoiceQuantity}</div>

                        <div class="body__label">Дата забора + период: </div>
                        <div class="body__value">${options.order.takeDate}</div>

                        <div class="body__label">Курьер: </div>
                        <div class="body__value">${courier}</div>

                        <div class="body__label">Статус : </div>
                        <div class="body__value">${status}</div>

                        <div class="body__label">Забрано в : </div>
                        <div class="body__value">${options.order.takenAt}</div>
                    </div>
                </div>`,
            }

            return map_graphics.createPoint(
                coordinates,
                {
                    // iconContent: options.order.invoiceNumber,
                    balloonContentHeader: props.balloonContentHeader,
                    balloonContentBody: props.balloonContentBody,
                },
                {
                    iconColor: options.color,
                }
            )
        }

        const getDeliveryPlacemark = (
            options: GetPlacemarkOptions & { order: DeliveryOrder }
        ): ymaps.Placemark => {
            const coordinates = [Number(options.order.latitude), Number(options.order.longitude)]
            const address = options.order.customer ? options.order.customer.address : 'Не указан'
            const courier = options.order.courier ? options.order.courier.fullName : 'Не указан'
            const status = options.order.status ? options.order.status.title : 'Не указан'
            const deliveredAt = options.order.deliveredAt ? options.order.deliveredAt : '-'

            const props = {
                balloonContentHeader: `<div class="newballoon__header">${options.order.invoiceNumber}</div>`,
                balloonContentBody: `<div class="newballoon__body">
                    <div class="grid grid-cols-2 gap-1 items-center">
                        <div class="body__label">Клиент: </div>
                        <div class="body__value">${options.order.companyName}</div>

                        <div class="body__label">Адрес: </div>
                        <div class="body__value">${address}</div>

                        <div class="body__label">Курьер: </div>
                        <div class="body__value">${courier}</div>

                        <div class="body__label">Статус : </div>
                        <div class="body__value">${status}</div>

                        <div class="body__label">Доставлено в : </div>
                        <div class="body__value">${deliveredAt}</div>
                    </div>
                </div>`,
            }

            return map_graphics.createPoint(
                coordinates,
                {
                    // iconContent: options.order.invoiceNumber,
                    balloonContentHeader: props.balloonContentHeader,
                    balloonContentBody: props.balloonContentBody,
                },
                {
                    iconColor: options.color,
                }
            )
        }

        const collectTakeCompletedPlacemarks = (): ymaps.Placemark[] => {
            if (!state.map_orders_types.includes(MAP_ORDERS_TYPES[1].id))
                return [] as ymaps.Placemark[]
            if (!takes_completed_orders_state.is_showed) return [] as ymaps.Placemark[]

            const placemarks = [] as ymaps.Placemark[]

            takes_completed_orders_state.orders.forEach((order) => {
                const placemark = getTakePlacemark({ order, color: PLACEMARK_COLORS.GREEN })
                placemarks.push(placemark)
            })

            return placemarks
        }

        const collectTakeRemainedPlacemarks = (): ymaps.Placemark[] => {
            if (!state.map_orders_types.includes(MAP_ORDERS_TYPES[1].id))
                return [] as ymaps.Placemark[]
            if (!takes_remained_orders_state.is_showed) return [] as ymaps.Placemark[]

            const placemarks = [] as ymaps.Placemark[]

            takes_remained_orders_state.orders.forEach((order) => {
                const placemark = getTakePlacemark({ order, color: PLACEMARK_COLORS.YELLOW })
                placemarks.push(placemark)
            })

            return placemarks
        }

        const collectTakeCancelledPlacemarks = (): ymaps.Placemark[] => {
            if (!state.map_orders_types.includes(MAP_ORDERS_TYPES[1].id))
                return [] as ymaps.Placemark[]
            if (!takes_cancelled_orders_state.is_showed) return [] as ymaps.Placemark[]

            const placemarks = [] as ymaps.Placemark[]

            takes_cancelled_orders_state.orders.forEach((order) => {
                const placemark = getTakePlacemark({ order, color: PLACEMARK_COLORS.RED })
                placemarks.push(placemark)
            })

            return placemarks
        }

        const collectDeliveryCompletedPlacemarks = (): ymaps.Placemark[] => {
            if (!state.map_orders_types.includes(MAP_ORDERS_TYPES[0].id))
                return [] as ymaps.Placemark[]
            if (!delivery_completed_orders_state.is_showed) return [] as ymaps.Placemark[]

            const placemarks = [] as ymaps.Placemark[]

            delivery_completed_orders_state.orders.forEach((order) => {
                const placemark = getDeliveryPlacemark({ order, color: PLACEMARK_COLORS.GREEN })
                placemarks.push(placemark)
            })

            return placemarks
        }

        const collectDeliveryRemainedPlacemarks = (): ymaps.Placemark[] => {
            if (!state.map_orders_types.includes(MAP_ORDERS_TYPES[0].id))
                return [] as ymaps.Placemark[]
            if (!delivery_remained_orders_state.is_showed) return [] as ymaps.Placemark[]

            const placemarks = [] as ymaps.Placemark[]

            delivery_remained_orders_state.orders.forEach((order) => {
                const placemark = getDeliveryPlacemark({ order, color: PLACEMARK_COLORS.YELLOW })
                placemarks.push(placemark)
            })

            return placemarks
        }

        const collectDeliveryCancelledPlacemarks = (): ymaps.Placemark[] => {
            if (!state.map_orders_types.includes(MAP_ORDERS_TYPES[0].id))
                return [] as ymaps.Placemark[]
            if (!delivery_cancelled_orders_state.is_showed) return [] as ymaps.Placemark[]

            const placemarks = [] as ymaps.Placemark[]

            delivery_cancelled_orders_state.orders.forEach((order) => {
                const placemark = getDeliveryPlacemark({ order, color: PLACEMARK_COLORS.RED })
                placemarks.push(placemark)
            })

            return placemarks
        }

        const renderMapOrders = () => {
            const cluster = map_graphics.createCluster({
                clusterIconLayout: 'default#pieChart',
                clusterIconPieChartRadius: 25,
                clusterIconPieChartCoreRadius: 10,
                clusterIconPieChartStrokeWidth: 3,
            })

            let placemarks = [] as any[]

            const take_completed_placemarks = collectTakeCompletedPlacemarks()
            const take_remained_placemarks = collectTakeRemainedPlacemarks()
            const take_cancelled_placemarks = collectTakeCancelledPlacemarks()

            const delivery_completed_placemarks = collectDeliveryCompletedPlacemarks()
            const delivery_remained_placemarks = collectDeliveryRemainedPlacemarks()
            const delivery_cancelled_placemarks = collectDeliveryCancelledPlacemarks()

            placemarks = [
                ...take_completed_placemarks,
                ...take_remained_placemarks,
                ...take_cancelled_placemarks,
                ...delivery_completed_placemarks,
                ...delivery_remained_placemarks,
                ...delivery_cancelled_placemarks,
            ]

            cluster.options.set({ clusterDisableClickZoom: true })
            cluster.add(placemarks)

            addGeoObject(map_instance as ymaps.Map, cluster)
        }

        const loadTakeCompletedOrders = async () => {
            takes_completed_orders_state.orders = []

            try {
                takes_completed_orders_state.is_loading = true

                let dispatcherSectorIds: number[] = []

                if (new_dispatcher_store.dispatcherSectorIds.length) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const response = await takeAPI.getTakeOrders({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds,
                    statusIds: [4],
                    dateFrom: filters.createdAtFrom,
                    dateTo: filters.createdAtTo,
                })

                takes_completed_orders_state.orders = response.data.data
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении завершенных заборов произошла ошибка',
                })
            } finally {
                takes_completed_orders_state.is_loading = false
            }
        }

        const loadTakeCancelledOrders = async () => {
            takes_cancelled_orders_state.orders = []

            try {
                takes_cancelled_orders_state.is_loading = true

                let dispatcherSectorIds: number[] = []

                if (new_dispatcher_store.dispatcherSectorIds.length) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const response = await takeAPI.getTakeOrders({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds,
                    statusIds: [5],
                    dateFrom: filters.createdAtFrom,
                    dateTo: filters.createdAtTo,
                })

                takes_cancelled_orders_state.orders = response.data.data
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении отмененных заборов произошла ошибка',
                })
            } finally {
                takes_cancelled_orders_state.is_loading = false
            }
        }

        const loadTakeRemainedOrders = async () => {
            takes_remained_orders_state.orders = []

            try {
                takes_remained_orders_state.is_loading = true

                let dispatcherSectorIds: number[] = []

                if (new_dispatcher_store.dispatcherSectorIds.length) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const response = await takeAPI.getTakeOrders({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds,
                    notInStatusIds: [4, 5],
                    dateFrom: filters.createdAtFrom,
                    dateTo: filters.createdAtTo,
                })

                takes_remained_orders_state.orders = response.data.data
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении остаточных заборов произошла ошибка',
                })
            } finally {
                takes_remained_orders_state.is_loading = false
            }
        }

        const loadDeliveryCompletedOrders = async () => {
            delivery_completed_orders_state.orders = []

            try {
                delivery_completed_orders_state.is_loading = true

                let dispatcherSectorIds: number[] = []

                if (new_dispatcher_store.dispatcherSectorIds) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const response = await deliveryAPI.getDeliveryOrders({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds,
                    statusIds: [23],
                    createdAtFrom: filters.createdAtFrom,
                    createdAtTo: filters.createdAtTo,
                })

                delivery_completed_orders_state.orders = response.data.data
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении завершенных доставок произошла ошибка',
                })
            } finally {
                delivery_completed_orders_state.is_loading = false
            }
        }

        const loadDeliveryCancelledOrders = async () => {
            delivery_cancelled_orders_state.orders = []

            try {
                delivery_cancelled_orders_state.is_loading = true

                let dispatcherSectorIds: number[] = []

                if (new_dispatcher_store.dispatcherSectorIds.length) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const response = await deliveryAPI.getDeliveryOrders({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds,
                    statusIds: [25, 27],
                    createdAtFrom: filters.createdAtFrom,
                    createdAtTo: filters.createdAtTo,
                })

                delivery_cancelled_orders_state.orders = response.data.data
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении отмененных доставок произошла ошибка',
                })
            } finally {
                delivery_cancelled_orders_state.is_loading = false
            }
        }

        const loadDeliveryRemainedOrders = async () => {
            delivery_remained_orders_state.orders = []

            try {
                delivery_remained_orders_state.is_loading = true

                let dispatcherSectorIds: number[] = []

                if (new_dispatcher_store.dispatcherSectorIds) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const response = await deliveryAPI.getDeliveryOrders({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds,
                    notInStatusIds: [23, 25, 27],
                    createdAtFrom: filters.createdAtFrom,
                    createdAtTo: filters.createdAtTo,
                })

                delivery_remained_orders_state.orders = response.data.data
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении остаточных доставок произошла ошибка',
                })
            } finally {
                delivery_remained_orders_state.is_loading = false
            }
        }

        const loadOrdersForMap = async () => {
            await loadTakeCompletedOrders()
            await loadTakeCancelledOrders()
            await loadTakeRemainedOrders()

            await loadDeliveryCompletedOrders()
            await loadDeliveryCancelledOrders()
            await loadDeliveryRemainedOrders()

            renderMapOrders()
        }

        const resetData = () => {
            filters.createdAtFrom = null
            filters.createdAtTo = null

            deliveries_state.stats = []
            takes_state.stats = []
            couriers_state.stats = []

            sectors_list_state.options = []

            takes_completed_orders_state.orders = []
            takes_completed_orders_state.is_showed = true
            takes_cancelled_orders_state.orders = []
            takes_cancelled_orders_state.is_showed = true
            takes_remained_orders_state.orders = []
            takes_remained_orders_state.is_showed = true

            delivery_completed_orders_state.orders = []
            delivery_completed_orders_state.is_showed = true
            delivery_cancelled_orders_state.orders = []
            delivery_cancelled_orders_state.is_showed = true
            delivery_remained_orders_state.orders = []
            delivery_cancelled_orders_state.is_showed = true

            clearMap(map_instance as ymaps.Map)
        }

        const loadData = () => {
            new_dispatcher_store.validation$.$reset()
            clearMap(map_instance as ymaps.Map)

            loadDeliveries()
            loadTakes()
            loadCouriers()

            // if (new_dispatcher_store.dispatcherSectors.length) {
            //     setMapCenterBySelectedDispatcherSector(
            //         new_dispatcher_store.dispatcherSectors as DispatcherSector[]
            //     )
            // }

            loadDeliverySectors()

            loadOrdersForMap()
        }

        const onMapReady = (instance: ymaps.Map) => {
            map_instance = instance
            state.is_map_ready = true

            loadData()
        }

        // New Dispatcher Sector Listener
        watch(
            () => new_dispatcher_store.dispatcherSectorIds,
            () => {
                loadData()
            }
        )

        const rerenderMapData = () => {
            if (!map_instance) return

            clearMap(map_instance)
            drawSectors(sectors_list_state.options)

            renderMapOrders()
        }

        const onCompletedTypeChange = (is_showed: boolean) => {
            delivery_completed_orders_state.is_showed = is_showed
            takes_completed_orders_state.is_showed = is_showed
            rerenderMapData()
        }

        const onCancelledTypeChange = (is_showed: boolean) => {
            delivery_cancelled_orders_state.is_showed = is_showed
            takes_cancelled_orders_state.is_showed = is_showed
            rerenderMapData()
        }

        const onRemainedTypeChange = (is_showed: boolean) => {
            delivery_remained_orders_state.is_showed = is_showed
            takes_remained_orders_state.is_showed = is_showed
            rerenderMapData()
        }

        const setTodayDate = () => {
            let today = new Date().toISOString().slice(0, 10)

            filters.createdAtFrom = today
            filters.createdAtTo = today
        }

        const isMapOrdersTypesDisabled = computed(() => {
            if (
                delivery_completed_orders_state.is_loading ||
                takes_completed_orders_state.is_loading
            )
                return true
            if (
                delivery_cancelled_orders_state.is_loading ||
                takes_cancelled_orders_state.is_loading
            )
                return true
            if (delivery_remained_orders_state.is_loading || takes_remained_orders_state.is_loading)
                return true
            return false
        })

        onBeforeMount(setTodayDate)

        return {
            MAP_ORDERS_TYPES,
            //
            state,
            filters,
            //
            deliveries_state,
            loadDeliveries,
            takes_state,
            loadTakes,
            couriers_state,
            loadCouriers,
            //
            delivery_completed_orders_state,
            delivery_cancelled_orders_state,
            delivery_remained_orders_state,
            //
            takes_completed_orders_state,
            takes_cancelled_orders_state,
            takes_remained_orders_state,
            //
            sectors_list_state,
            loadDeliverySectors,
            loadData,
            resetData,
            //
            onMapReady,
            rerenderMapData,
            onCompletedTypeChange,
            onCancelledTypeChange,
            onRemainedTypeChange,
            setMapCenterOnSector,
            //
            isMapOrdersTypesDisabled,
        }
    },
})
